import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, call, setTokenSource } from '@/api/client';
import { isApiError } from '@/api/errors';
import type { ProfileResponse, SignInResponse } from '@/api/schema';
import { env, firebaseConfigured } from '@/lib/env';
import { qk } from '@/api/keys';
import { useThemeStore, type ThemePreference } from '@/theme/theme';
import { gates } from '@/app/gates';

export type SessionStatus = 'loading' | 'signedOut' | 'needsVerification' | 'needsCountry' | 'signedIn' | 'unconfigured';

interface SessionValue {
  status: SessionStatus;
  /** The sign-in exchange failed for a reason other than a missing country. */
  error: unknown;
  /** Finishes account creation for a first sign-in (country is mandatory, requirements §1.12). */
  /**
   * Creates the account. `gettingStartedEmails: false` is the refusal the sign-up step offers
   * (PECR's soft opt-in, `docs/ops/legitimate_interests.md` §4.6); omitted, the default (on) stands.
   */
  completeSignUp: (countryCode: string, gettingStartedEmails?: boolean) => Promise<void>;
  /**
   * Finishes an email sign-in that stopped at verification: re-checks the address, then runs the
   * exchange. False while the link has not been followed (the status stays `needsVerification`).
   */
  confirmEmailVerified: () => Promise<boolean>;
  /** Re-runs the exchange after a failure, with the Firebase session still in place. */
  retry: () => Promise<void>;
  signOut: () => Promise<void>;
  /** The Firebase user signs in with email and password, so Account offers Change password. */
  passwordSignIn: boolean;
  /** Mails the signed-in address a link to set a new password. */
  sendPasswordChange: () => Promise<void>;
  devMode: boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

/** Sign-in returns `{user, entitlements, outstanding_legal}`; the profile is flat. Normalise. */
function flatten(r: SignInResponse): ProfileResponse {
  return {
    ...r.user,
    entitlements: r.entitlements,
    outstanding_legal: r.outstanding_legal,
    setup_needed: r.setup_needed,
  };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const devMode = Boolean(env.devAuthToken);
  const [status, setStatus] = useState<SessionStatus>(
    devMode || firebaseConfigured ? 'loading' : 'unconfigured',
  );
  const [error, setError] = useState<unknown>(null);
  const [passwordSignIn, setPasswordSignIn] = useState(false);
  // The Firebase user the last exchange was for. Token refreshes re-fire the auth listener for
  // the same uid and must not re-exchange; a *failed* exchange clears it, so the next event for
  // that uid (or a retry) exchanges again instead of being ignored as a refresh.
  const lastUid = useRef<string | null>(null);

  const exchange = useCallback(
    async (countryCode?: string, gettingStartedEmails?: boolean) => {
      try {
        const body = {
          ...(countryCode ? { country_code: countryCode } : {}),
          ...(gettingStartedEmails === false ? { lifecycle_emails_enabled: false } : {}),
        };
        const res = await call(api.POST('/api/v1/auth/sign-in', { body }));
        const profile = flatten(res);
        queryClient.setQueryData(qk.me, profile);
        if (profile.outstanding_legal.length > 0) gates.getState().setLegalRequired();
        setError(null);
        setStatus('signedIn');
      } catch (e) {
        if (isApiError(e) && e.code === 'country_code_required') {
          setStatus('needsCountry');
          return;
        }
        // A new email/password account: the server creates nothing until the address is verified
        // (services/auth.py step 0). The Firebase session is kept, and `lastUid` with it, so the
        // token refresh that confirming forces is not taken for a new sign-in.
        if (isApiError(e) && e.code === 'email_not_verified') {
          setStatus('needsVerification');
          return;
        }
        lastUid.current = null;
        setError(e);
        setStatus('signedOut');
      }
    },
    [queryClient],
  );

  useEffect(() => {
    if (devMode) {
      setTokenSource(async () => env.devAuthToken ?? null);
      // The exchange is a request; its state updates land after it resolves, not synchronously.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void exchange();
      return;
    }
    if (!firebaseConfigured) return;
    let unsubscribe = () => {};
    let cancelled = false;
    void import('./firebase').then(({ onUserChanged, currentToken, hasPasswordSignIn }) => {
      if (cancelled) return;
      setTokenSource(currentToken);
      unsubscribe = onUserChanged((user) => {
        setPasswordSignIn(user ? hasPasswordSignIn(user) : false);
        if (!user) {
          lastUid.current = null;
          queryClient.clear();
          setStatus('signedOut');
          return;
        }
        // Token refreshes fire this too; only a new user needs a new exchange.
        if (user.uid === lastUid.current) return;
        lastUid.current = user.uid;
        setStatus('loading');
        void exchange();
      });
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [devMode, exchange, queryClient]);

  const signOut = useCallback(async () => {
    // Before the session ends, while the API still accepts this user's token: a signed-out
    // browser must not keep receiving the account's alerts (lib/push.ts).
    const { forgetPushOnSignOut } = await import('@/lib/push');
    await forgetPushOnSignOut().catch(() => undefined);
    if (devMode) {
      queryClient.clear();
      setStatus('signedOut');
      return;
    }
    const { signOutFirebase } = await import('./firebase');
    await signOutFirebase();
  }, [devMode, queryClient]);

  const retry = useCallback(async () => {
    setStatus('loading');
    await exchange();
  }, [exchange]);

  const confirmEmailVerified = useCallback(async () => {
    const { refreshEmailVerification } = await import('./firebase');
    if (!(await refreshEmailVerification())) return false;
    await exchange();
    return true;
  }, [exchange]);

  const sendPasswordChange = useCallback(async () => {
    const { sendPasswordChange: send } = await import('./firebase');
    await send();
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      status,
      error,
      completeSignUp: (c, emails) => exchange(c, emails),
      confirmEmailVerified,
      retry,
      signOut,
      passwordSignIn,
      sendPasswordChange,
      devMode,
    }),
    [status, error, exchange, confirmEmailVerified, retry, signOut, passwordSignIn, sendPasswordChange, devMode],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

/** Keeps the theme in step with the account's saved preference. */
export function useSyncThemeFromProfile(pref: string | undefined) {
  const setPreference = useThemeStore((s) => s.setPreference);
  useEffect(() => {
    if (pref === 'dark' || pref === 'light' || pref === 'system') setPreference(pref as ThemePreference);
  }, [pref, setPreference]);
}
